//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import { fetchJobById } from '../../common/services/jobCenterServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _find from 'lodash/find';
import { getFormattedDate, getFormattedTime } from '../../common/dateHelper';
import { updateJob } from '../../common/services/empMyJobsServices';
import { LayoutContext } from '../../common/contexts';
import { loadEmployerData } from '../../common/services/appServices';
import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { getFullNameByUser } from '../../common/textHelper';
import { loadProfile } from '../../common/services/empDashboardServices';
import { getEmployerDataForActiveJob } from '../../common/employerHelper';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: { margin: 24 },
        fieldContainer: { display: 'flex', flexDirection: 'column', width: 400 },
        buttonContainer: { marginTop: 8 },
        jobDetailContainer: { marginTop: 14 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        jobId,
        handleChangeJobIdInput,
        handleClickSearchButton,
        handleClickToggleActiceButton,
    } = params;

    const defns = {
        jobIdSearchInput: { value: jobId, onChange: handleChangeJobIdInput },
        searchButton: {
            color: 'secondary',
            onClick: handleClickSearchButton,
        },
        toggleActiveButton: {
            color: 'secondary',
            onClick: handleClickToggleActiceButton,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const FieldLabel = props => {
    const { children } = props;
    return (
        <AfitdTypography variant="body2" clsLightBold>
            {children}
        </AfitdTypography>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobListingStatusEdit = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [jobId, setJobId] = useState('MPxMcOeyDZikkGzrUwSq');
    const [jobList, setJobList] = useState();
    const [employer, setEmployer] = useState();
    const [empProfileData, setEmpProfileData] = useState();
    const [ranSearch, setRanSearch] = useState(false);
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const job = _get(jobList, '[0]');
    const jobTitle = job?.jobTitle;
    const employerId = job?.employerId;
    const empMembers = employer?.members;
    const ownerName = getFullNameByUser(_find(empMembers, member => member?.isOwner));

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (employerId) {
            const unsubscribeLoadEmployerData = loadEmployerData({ setEmployer, employerId });

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates(unsubscribeLoadEmployerData);
            };
        } else {
            setEmployer(null);
        }
    }, [employerId]);

    useEffect(() => {
        let unsubscribeOnSnapshot = null;

        // load profile data
        unsubscribeOnSnapshot = loadProfile(employerId, setEmpProfileData);

        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeOnSnapshot]);
        };
    }, [employerId]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeJobIdInput = evt => {
        const value = evt.target.value;
        setJobId(value);
    };

    const handleClickSearchButton = () => {
        fetchJobById(jobId, setJobList);
        setRanSearch(true);
    };

    const handleClickToggleActiceButton = async () => {
        const employerData = getEmployerDataForActiveJob({ employer, profile: empProfileData });

        await updateJob({
            data: { ...job, ...employerData, active: !job?.active },
            activeOrig: job?.active,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });

        fetchJobById(jobId, setJobList);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, jobId, handleChangeJobIdInput, handleClickSearchButton, handleClickToggleActiceButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.fieldContainer}>
                <FieldLabel>Job ID</FieldLabel>
                <AfitdTextField {...defns.jobIdSearchInput} />
            </div>
            <div {...defns.buttonContainer}>
                <AfitdButton color="secondary" {...defns.searchButton}>
                    Search
                </AfitdButton>
            </div>
            {ranSearch ? (
                jobTitle ? (
                    <div {...defns.jobDetailContainer}>
                        <AfitdTypography variant="h6">{jobTitle}</AfitdTypography>
                        <AfitdTypography variant="body2">{job?.companyName}</AfitdTypography>
                        <AfitdTypography variant="body2">
                            Created on {getFormattedDate(job?.createDt)} {getFormattedTime(job?.createDt)}
                        </AfitdTypography>
                        <AfitdTypography variant="body2">{job?.active ? 'ACTIVE' : 'INACTIVE'}</AfitdTypography>

                        <AfitdTypography variant="body2">Owner Name: {ownerName}</AfitdTypography>
                        <div {...defns.buttonContainer}>
                            <AfitdButton color="secondary" {...defns.toggleActiveButton}>
                                {job?.active ? 'Set Inactive' : 'Set Active'}
                            </AfitdButton>
                        </div>
                    </div>
                ) : (
                    <AfitdTypography variant="body1" style={{ marginTop: 8 }}>
                        Job not found.
                    </AfitdTypography>
                )
            ) : null}
        </div>
    );
};

export default JobListingStatusEdit;
